import DataPointsLib, { type DataPoints } from '@retracedgmbh/data-points-lib';
import type { Template } from '../../db/templates/types.js';
import {
  AUDIT_DATA_POINT_ID_PREFIX,
  EXCLUDED_NETWORK_EXPORT_DATA_POINT_IDS,
} from './constants.js';
import type { IDataPointDefinition } from './types.js';

/**
 * `data-points-lib`, initialised once per process.
 *
 * Same setup as `data-point-catalogue.service.ts`: the library reads no
 * configuration beyond its own bundled definitions, so a module-level instance
 * carries no request state — and building one per call would re-read 2099
 * definition modules every time the picker or an export runs.
 */
const dataPointsLib: DataPoints = new DataPointsLib(
  console,
).initializeDataPoints();

/**
 * Every data point the network export may emit, in template order.
 *
 * Deliberately *not* the questionnaire catalogue. That one applies
 * `DISABLED_DATAPOINTS_FOR_QUESTIONNAIRES`, which encodes what makes a sensible
 * questionnaire *question* rather than what belongs in an export, and it lives in
 * `modules/questionnaires` — importing it from here would close a dependency
 * cycle, since it already imports `listTemplatesUnscopedDb` from this module.
 *
 * Exclusions are applied to the id, before resolution: `audit_*` is a pure prefix
 * test, as there is no flag on the definition marking an audit data point.
 *
 * Takes the template rows rather than fetching them, because every caller needs
 * the same rows for grouping and one read should serve both.
 */
export const resolveNetworkExportCatalogueIds = (
  templates: Template[],
): string[] => {
  const ids: string[] = [];
  const seen = new Set<string>();

  for (const id of templates.flatMap(template => template.composedOf)) {
    if (seen.has(id)) continue;
    seen.add(id);

    if (id.startsWith(AUDIT_DATA_POINT_ID_PREFIX)) continue;
    if (EXCLUDED_NETWORK_EXPORT_DATA_POINT_IDS.has(id)) continue;

    ids.push(id);
  }

  return ids;
};

/**
 * The definitions for those ids, indexed by `retracedId`.
 *
 * `getDataPointById` throws on an id the library does not know. The questionnaire
 * catalogue leaves that unguarded on purpose — a half-populated builder sidebar
 * is indistinguishable from a working one. An export needs the opposite bias: one
 * template referencing a data point a library downgrade no longer carries must
 * cost that column, not the whole workbook.
 */
export const loadNetworkExportDefinitions = (
  retracedDataPointIds: string[],
): Map<string, IDataPointDefinition> => {
  const definitions = new Map<string, IDataPointDefinition>();

  for (const id of retracedDataPointIds) {
    try {
      const definition = dataPointsLib.getDataPointById(id)
        .dataPointDefinition as unknown as IDataPointDefinition;

      if (definition?.retracedId) {
        definitions.set(definition.retracedId, definition);
      }
    } catch {
      // Unknown to the library — the column is dropped, the export continues.
    }
  }

  return definitions;
};

/**
 * A data point's choice options as a `key -> label` map. `{}` for an unknown id
 * or a field with no options, so callers fall back to the stored key.
 */
export const loadChoiceOptionLabels = (
  retracedDataPointId: string,
): Record<string, string> => {
  const labels: Record<string, string> = {};

  try {
    const definition = dataPointsLib.getDataPointById(retracedDataPointId)
      .dataPointDefinition as unknown as IDataPointDefinition;

    for (const option of definition?.options ?? []) {
      if (option.key && option.text) {
        labels[option.key] = option.text;
      }
    }
  } catch {
    // Unknown to the library — the caller keeps the stored key.
  }

  return labels;
};
